import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { listAccounts } from '../accounts/api';
import { ApiError } from '../../lib/api';
import type {
  AudiencePreviewResult,
  AudienceScope,
  Broadcast,
  BroadcastChannel,
  BroadcastSenderMode,
} from '../../types/broadcast';
import { AudiencePanel } from './AudiencePanel';
import { MarkdownEditor } from './MarkdownEditor';
import { SlugTextField } from './SlugTextField';
import { SenderSelect } from './SenderSelect';
import { BroadcastPreviewPane } from './BroadcastPreviewPane';
import { ConfirmSendModal } from './ConfirmSendModal';
import { RecipientPreviewModal } from './RecipientPreviewModal';
import {
  createBroadcast,
  getBroadcast,
  previewAudience,
  sendBroadcast,
  updateBroadcast,
} from './api';
import {
  AUTOSAVE_DEBOUNCE_MS,
  computeSaveStatus,
  SAVE_STATUS_LABEL,
  shouldAutosave,
} from '../../lib/broadcastAutosave';

// Stage 6A §5 — broadcast compose page. Three-pane layout: audience (left),
// content (center), live preview (right). Reached at /broadcasts/new (create)
// and /broadcasts/:id/edit (load an existing draft). Save Draft creates on the
// first save then updates; Send Now lands in A6.

export type ComposeDraft = {
  title: string;
  audienceScope: AudienceScope;
  channels: BroadcastChannel[];
  emailSubject: string;
  emailBodyMarkdown: string;
  smsBody: string;
  emailFromMode: BroadcastSenderMode;
  emailFromCustomName: string | null;
  emailReplyToMode: BroadcastSenderMode;
  emailReplyToCustomEmail: string | null;
};

export function emptyAudienceScope(brandAccountId = ''): AudienceScope {
  return {
    brandSelection: { brandAccountIds: brandAccountId ? [brandAccountId] : [] },
    selectedAccountIds: [],
    excludedAccountIds: [],
    contactLevelFilter: [],
    commsPurposeFilter: [],
    accountManagerFilter: [],
  };
}

function emptyDraft(brandAccountId = ''): ComposeDraft {
  return {
    title: '',
    audienceScope: emptyAudienceScope(brandAccountId),
    channels: ['email'],
    emailSubject: '',
    emailBodyMarkdown: '',
    smsBody: '',
    emailFromMode: 'account_manager',
    emailFromCustomName: null,
    emailReplyToMode: 'account_manager',
    emailReplyToCustomEmail: null,
  };
}

// Map a loaded broadcast into the editable draft shape (server-owned fields
// like status / counters / timestamps are dropped).
function broadcastToDraft(b: Broadcast): ComposeDraft {
  return {
    title: b.title,
    audienceScope: b.audienceScope,
    channels: b.channels,
    emailSubject: b.emailSubject,
    emailBodyMarkdown: b.emailBodyMarkdown,
    smsBody: b.smsBody,
    emailFromMode: b.emailFromMode,
    emailFromCustomName: b.emailFromCustomName,
    emailReplyToMode: b.emailReplyToMode,
    emailReplyToCustomEmail: b.emailReplyToCustomEmail,
  };
}

// Brand presence — true for 'all', or when ≥1 specific brand is chosen.
function hasBrand(scope: AudienceScope): boolean {
  return scope.brandSelection === 'all' || scope.brandSelection.brandAccountIds.length > 0;
}

export function BroadcastCompose() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id: routeId } = useParams<{ id?: string }>();
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  // Edit mode: load the existing draft and seed the form once.
  const existing = useQuery({
    queryKey: ['broadcast', routeId],
    queryFn: () => getBroadcast(routeId!),
    enabled: Boolean(routeId),
  });
  const [broadcastId, setBroadcastId] = useState<string | null>(routeId ?? null);
  const [draft, setDraft] = useState<ComposeDraft>(() => emptyDraft(params.get('brand') ?? ''));
  const seeded = useRef(false);

  // Autosave bookkeeping (all serialized-draft snapshots so dirty checks compare
  // like-for-like, sidestepping object key-order pitfalls):
  //   lastSavedSnapshotRef  — JSON of the draft as last persisted. For a new
  //     draft this starts as the pristine empty form (so it isn't "dirty" until
  //     edited); in edit mode it's set from the loaded broadcast on seed.
  //   pendingSnapshotRef    — JSON of the draft handed to the in-flight save.
  //   errorSnapshotRef      — JSON of the content whose save failed, so autosave
  //     doesn't retry-loop the same failing payload every 2.5s.
  const lastSavedSnapshotRef = useRef<string | null>(
    routeId ? null : JSON.stringify(emptyDraft(params.get('brand') ?? '')),
  );
  const pendingSnapshotRef = useRef<string | null>(null);
  const errorSnapshotRef = useRef<string | null>(null);

  useEffect(() => {
    if (routeId && existing.data && !seeded.current) {
      const seededDraft = broadcastToDraft(existing.data);
      setDraft(seededDraft);
      lastSavedSnapshotRef.current = JSON.stringify(seededDraft);
      seeded.current = true;
    }
  }, [routeId, existing.data]);

  const update = (patch: Partial<ComposeDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const draftJson = JSON.stringify(draft);
  const isDirty = lastSavedSnapshotRef.current !== draftJson;

  const [saveError, setSaveError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      // Snapshot exactly what we're persisting so onSuccess can mark it clean.
      pendingSnapshotRef.current = JSON.stringify(draft);
      return broadcastId ? updateBroadcast(broadcastId, draft) : createBroadcast(draft);
    },
    onSuccess: (saved) => {
      setSaveError(null);
      errorSnapshotRef.current = null;
      lastSavedSnapshotRef.current = pendingSnapshotRef.current;
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      qc.setQueryData(['broadcast', saved.id], saved);
      if (!broadcastId) {
        setBroadcastId(saved.id);
        seeded.current = true; // we already hold the freshest state
        navigate(`/broadcasts/${saved.id}/edit`, { replace: true });
      }
    },
    onError: (err) => {
      // Remember the failed payload so autosave doesn't hammer it on a loop;
      // editing the draft clears this (the content no longer matches).
      errorSnapshotRef.current = pendingSnapshotRef.current;
      setSaveError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const canSave = draft.title.trim().length > 0 && hasBrand(draft.audienceScope);

  // Send Now: persist the latest draft first (so the sent content matches the
  // composer), then open the confirmation dialog.
  const [showConfirm, setShowConfirm] = useState(false);
  const send = useMutation({
    mutationFn: (sendId: string) => sendBroadcast(sendId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      setShowConfirm(false);
      navigate('/broadcasts');
    },
    onError: (err) => setSaveError(err instanceof ApiError ? err.message : String(err)),
  });
  const onSendClick = async () => {
    setSaveError(null);
    try {
      const saved = await save.mutateAsync();
      setBroadcastId(saved.id);
      setShowConfirm(true);
    } catch {
      /* save error already surfaced via save.onError */
    }
  };

  // Debounced autosave (ux-debt §"Stage 6A" — compose autosave). Once the draft
  // is meaningful (title + brand) and differs from what's persisted, save it
  // ~2.5s after the last edit through the same create-then-update path. The
  // timer resets on every keystroke (effect cleanup), so it only fires on idle.
  const retryingFailedContent = errorSnapshotRef.current === draftJson;
  useEffect(() => {
    if (
      !shouldAutosave({
        canSave,
        isDirty,
        saving: save.isPending,
        sending: send.isPending,
        confirming: showConfirm,
        retryingFailedContent,
      })
    ) {
      return;
    }
    const t = window.setTimeout(() => save.mutate(), AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draftJson captures the draft; save/send are stable
  }, [draftJson, canSave, isDirty, save.isPending, send.isPending, showConfirm, retryingFailedContent]);

  const saveStatusLabel =
    SAVE_STATUS_LABEL[
      computeSaveStatus({ saving: save.isPending, isDirty, hasPersisted: Boolean(broadcastId) })
    ];

  // Recipient preview modal (§6). "Send now" inside it routes through the same
  // save-then-confirm flow.
  const [showRecipients, setShowRecipients] = useState(false);

  const scope = draft.audienceScope;
  const brandPresent = hasBrand(scope);
  const onScopeChange = (next: AudienceScope) => update({ audienceScope: next });

  // Single debounced audience query, shared by the left-pane counters and the
  // right-pane recipient picker (React Query dedupes by key).
  const scopeKey = JSON.stringify(scope);
  const [debouncedScope, setDebouncedScope] = useState<AudienceScope>(scope);
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedScope(scope), 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeKey captures scope
  }, [scopeKey]);

  const audienceQuery = useQuery({
    queryKey: ['broadcast-audience', debouncedScope],
    queryFn: () => previewAudience(debouncedScope),
    enabled: hasBrand(debouncedScope),
  });

  if (accountsQuery.isLoading || (routeId && existing.isLoading)) return <CenteredSpinner />;
  if (routeId && existing.error) {
    return <p className="text-sm text-red-600">Couldn't load this broadcast.</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{broadcastId ? 'Edit broadcast' : 'New broadcast'}</h1>
        <div className="flex items-center gap-2">
          {saveError ? (
            <span className="text-sm text-red-600">{saveError}</span>
          ) : (
            saveStatusLabel && <span className="text-sm text-slate-500">{saveStatusLabel}</span>
          )}
          <Button
            variant="outline"
            disabled={!canSave || save.isPending}
            onClick={() => save.mutate()}
            title={canSave ? undefined : 'A title and a brand are required to save.'}
          >
            {save.isPending ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            disabled={!canSave || save.isPending || send.isPending}
            onClick={onSendClick}
            title={canSave ? undefined : 'A title and a brand are required to send.'}
          >
            Send now
          </Button>
          <Button variant="outline" onClick={() => navigate('/broadcasts')}>
            Cancel
          </Button>
        </div>
      </div>

      {showConfirm && (
        <ConfirmSendModal
          draft={draft}
          audience={audienceQuery.data}
          sending={send.isPending}
          sendError={send.error instanceof ApiError ? send.error.message : null}
          onCancel={() => setShowConfirm(false)}
          onConfirm={() => broadcastId && send.mutate(broadcastId)}
        />
      )}

      {showRecipients && (
        <RecipientPreviewModal
          queryKey={['broadcast-audience-details', scope]}
          fetchDetails={() => previewAudience(scope, true)}
          onClose={() => setShowRecipients(false)}
          onSendNow={() => {
            setShowRecipients(false);
            void onSendClick();
          }}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[20rem_minmax(0,1fr)_24rem]">
        {/* Left — audience */}
        <section className="rounded-md border border-slate-200 bg-white">
          <header className="border-b border-slate-200 px-4 py-3">
            <h2 className="font-semibold">Audience</h2>
          </header>
          <div className="space-y-3 p-4">
            <AudiencePanel scope={scope} onChange={onScopeChange} />
          </div>
          {brandPresent && (
            <div>
              <AudienceCounters
                result={audienceQuery.data}
                loading={audienceQuery.isLoading}
                isError={audienceQuery.isError}
              />
              <div className="px-4 pb-3">
                <button
                  type="button"
                  onClick={() => setShowRecipients(true)}
                  className="text-sm text-slate-600 underline hover:text-slate-900"
                >
                  Preview audience →
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Center — content (filled in A4.3 / A4.4) */}
        <section className="rounded-md border border-slate-200 bg-white">
          <header className="border-b border-slate-200 px-4 py-3">
            <h2 className="font-semibold">Compose</h2>
          </header>
          <div className="space-y-4 p-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">
                Title <span className="text-slate-400">(internal label)</span>
              </span>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="May newsletter — Snap AU"
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              />
            </label>

            <ChannelToggle
              channels={draft.channels}
              onChange={(channels) => update({ channels })}
            />

            {draft.channels.includes('email') && (
              <div className="space-y-3 rounded-md border border-slate-200 p-3">
                <h3 className="text-sm font-semibold text-slate-700">Email</h3>
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-slate-700">Subject</span>
                  <SlugTextField
                    value={draft.emailSubject}
                    onChange={(emailSubject) => update({ emailSubject })}
                    placeholder="Your {brand_name} update for {current_month}"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-slate-700">Body</span>
                  <MarkdownEditor
                    value={draft.emailBodyMarkdown}
                    onChange={(emailBodyMarkdown) => update({ emailBodyMarkdown })}
                    placeholder={'Hi {first_name},\n\n…'}
                  />
                </label>
                <SenderSelect
                  value={draft}
                  onChange={(patch) => update(patch)}
                />
              </div>
            )}

            {draft.channels.includes('sms') && (
              <div className="space-y-3 rounded-md border border-slate-200 p-3">
                <h3 className="text-sm font-semibold text-slate-700">SMS</h3>
                <SlugTextField
                  value={draft.smsBody}
                  onChange={(smsBody) => update({ smsBody })}
                  multiline
                  rows={5}
                  smsCounter
                  placeholder="Hi {first_name}, …"
                />
              </div>
            )}
          </div>
        </section>

        {/* Right — preview (filled in A4.5) */}
        <section className="rounded-md border border-slate-200 bg-white">
          <header className="border-b border-slate-200 px-4 py-3">
            <h2 className="font-semibold">Preview</h2>
          </header>
          <div className="p-4">
            {brandPresent ? (
              <BroadcastPreviewPane
                draft={draft}
                recipients={audienceQuery.data?.recipients ?? []}
                audienceLoading={audienceQuery.isLoading}
              />
            ) : (
              <p className="text-sm text-slate-400">Pick a brand and audience to preview content.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// Email / SMS / Both segmented control mapping to the channels[] array.
function ChannelToggle({
  channels,
  onChange,
}: {
  channels: BroadcastChannel[];
  onChange: (next: BroadcastChannel[]) => void;
}) {
  const has = (c: BroadcastChannel) => channels.includes(c);
  const mode: 'email' | 'sms' | 'both' =
    has('email') && has('sms') ? 'both' : has('sms') ? 'sms' : 'email';
  const options: Array<{ key: 'email' | 'sms' | 'both'; label: string; value: BroadcastChannel[] }> = [
    { key: 'email', label: 'Email', value: ['email'] },
    { key: 'sms', label: 'SMS', value: ['sms'] },
    { key: 'both', label: 'Both', value: ['email', 'sms'] },
  ];
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-slate-700">Channels</span>
      <div className="inline-flex rounded-md border border-slate-300 p-0.5">
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.value)}
            className={`rounded px-3 py-1 text-sm ${
              mode === o.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Live audience counters (§2.2). Presentational — fed by the page-level
// audience query so the count + the preview recipient list share one fetch.
function AudienceCounters({
  result,
  loading,
  isError,
}: {
  result: AudiencePreviewResult | undefined;
  loading: boolean;
  isError: boolean;
}) {
  return (
    <div className="border-t border-slate-200 px-4 py-3 text-sm">
      {loading ? (
        <span className="text-slate-500">Counting audience…</span>
      ) : isError ? (
        <span className="text-red-600">Couldn't count audience.</span>
      ) : result ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
          <Counter label="Accounts" value={result.totalAccounts} />
          <Counter label="Contacts" value={result.totalContacts} />
          <Counter label="Email-reachable" value={result.emailReachable} />
          <Counter label="SMS-reachable" value={result.smsReachable} />
        </dl>
      ) : null}
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
